import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import SplitText from 'gsap/SplitText';
gsap.registerPlugin( CustomEase, SplitText );

import { eventBus } from './event-bus';

export const generateAnimations = ( theElement, animations ) => {
	if ( ! animations?.length ) return [];
	const timelines = [];
	const textInstances = [];
	animations.forEach( ( animation, index ) => {
		const animationID = animation.animationID;
		const [ timeline, textInstance ] = generateAnimation(
			theElement,
			animation
		);
		timelines[ index ] = { animationID, timeline };
		textInstances[ index ] = { animationID, textInstance };
	} );
	return [ timelines, textInstances ];
};

export const generateAnimation = ( theElement, animation ) => {
	const textInstance = [];
	const timelineAttrs = { paused: true };
	if ( animation.repeat ) {
		timelineAttrs.repeat = animation.repeat;
	}
	const theClips = animation.clips;
	const timeline = gsap.timeline( timelineAttrs );
	theClips.forEach( ( clip, index ) => {
		let theTargets = [];
		if ( clip.target ) {
			theTargets = [];
			clip.target.forEach( ( ele ) => {
				const getTargets = clip.child
					? gsap.utils.toArray( ele, theElement )
					: gsap.utils.toArray( ele );
				theTargets = theTargets.concat( getTargets );
			} );
		} else if ( theElement ) {
			theTargets = [ theElement ];
		}
		const tlPosition = `<${ clip.delayPrevious }`;
		switch ( clip.clipType ) {
			case 'element':
				const generalProps = {
					duration: clip.duration || 0.5,
				};
				if ( clip.customEase ) {
					generalProps.ease = CustomEase.create(
						'custom',
						clip.customEase
					);
				} else if ( clip.ease ) {
					generalProps.ease = clip.ease;
				} else {
					generalProps.ease = 'power1.inOut';
				}
				if ( clip.stagger ) generalProps.stagger = clip.stagger;
				switch ( clip.method ) {
					case 'set':
						timeline.set( theTargets, { ...clip.to }, tlPosition );
						break;
					case 'from':
						timeline.from(
							theTargets,
							{
								...generalProps,
								...clip.to,
							},
							tlPosition
						);
						break;
					case 'fromTo':
						timeline.fromTo(
							theTargets,
							clip.from,
							{
								...generalProps,
								...clip.to,
							},
							tlPosition
						);
						break;
					default:
						timeline.to(
							theTargets,
							{
								...generalProps,
								...clip.to,
							},
							tlPosition
						);
				}
				break;
			case 'text':
				let textTargets = [];
				const textElement = clip.textElement || 'lines';
				theTargets.forEach( ( target ) => {
					let theTextElements =
						target.querySelectorAll( '.mk-split-line' );
					if ( textElement === 'words' ) {
						theTextElements =
							target.querySelectorAll( '.mk-split-word' );
					} else if ( textElement === 'chars' ) {
						theTextElements =
							target.querySelectorAll( '.mk-split-char' );
					}
					if ( ! theTextElements?.length ) {
						const theTextInstance = new SplitText( target, {
							type: textElement,
							linesClass: 'mk-split-line',
							wordsClass: 'mk-split-word',
							charsClass: 'mk-split-char',
						} );
						textInstance.push( theTextInstance );
						textTargets = theTextInstance[ textElement ];
					} else {
						textTargets = theTextElements;
					}
				} );
				const generalTextProps = {
					duration: clip.duration || 0.5,
				};
				if ( clip.customEase ) {
					generalTextProps.ease = CustomEase.create(
						'custom',
						clip.customEase
					);
				} else if ( clip.ease ) {
					generalTextProps.ease = clip.ease;
				} else {
					generalTextProps.ease = 'power1.inOut';
				}
				if ( clip.stagger ) generalTextProps.stagger = clip.stagger;
				switch ( clip.method ) {
					case 'set':
						timeline.set( textTargets, { ...clip.to }, tlPosition );
						break;
					case 'from':
						timeline.from(
							textTargets,
							{
								...generalTextProps,
								...clip.to,
							},
							tlPosition
						);
						break;
					case 'fromTo':
						timeline.fromTo(
							textTargets,
							clip.from,
							{
								...generalTextProps,
								...clip.to,
							},
							tlPosition
						);
						break;
					default:
						timeline.to(
							textTargets,
							{
								...generalTextProps,
								...clip.to,
							},
							tlPosition
						);
				}
				break;
			case 'lottie':
				const lottieTarget = clip.target?.[ 0 ];
				const lottieID = lottieTarget?.slice( 1 ) || '';
				let lottieAction;
				if ( clip.method === 'sync' ) {
					const frameFrom = clip.frameFrom;
					const frameTo = clip.frameTo;
					const frameInter = frameTo - frameFrom;
					lottieAction = {
						onUpdate: () => {
							const progress = getTweenProgress(
								timeline,
								timeline.getChildren()[ index ]
							);
							let currentFrame = Math.round(
								frameFrom + progress * frameInter
							);
							if ( currentFrame <= frameFrom )
								currentFrame = frameFrom + 1;
							if ( currentFrame >= frameTo )
								currentFrame = frameTo - 1;
							eventBus.emit(
								`lottie-${ lottieID }-to`,
								currentFrame
							);
						},
					};
				}
				timeline.to(
					theElement,
					{
						duration: clip.duration,
						...lottieAction,
					},
					tlPosition
				);
				break;
			case 'video':
				let videoTarget = [];
				if ( clip.target ) {
					videoTarget = clip.target?.[ 0 ];
				} else if ( theElement ) {
					videoTarget = theElement;
				}
				if ( videoTarget.tagName !== 'VIDEO' ) {
					videoTarget = videoTarget.querySelector( 'video' );
				}
				let videoAction;
				const timeFrom = clip.isJump ? clip.timeFrom : 0;
				if ( clip.method === 'sync' ) {
					const videoTo = gsap.quickTo( videoTarget, 'currentTime', {
						duration: 1,
					} );
					const timeTo = clip.isJump ? clip.timeTo : clip.totalTime;
					const timeInter = timeTo - timeFrom;
					videoAction = {
						onStart: () => {
							videoTarget.pause();
						},
						onUpdate: () => {
							const videoProgress = getTweenProgress(
								timeline,
								timeline.getChildren()[ index ]
							);
							let currentTime =
								Math.round(
									( timeFrom + videoProgress * timeInter ) *
										100
								) / 100;
							if ( currentTime <= timeFrom )
								currentTime = timeFrom;
							if ( currentTime >= timeTo ) currentTime = timeTo;
							videoTo( currentTime );
						},
					};
				} else if ( clip.method === 'play' ) {
					videoAction = {
						onStart: () => {
							if ( clip.isJump ) {
								const currentTime = clip.timeFrom || 0;
								videoTarget.currentTime = currentTime;
							}
							videoTarget.play();
						},
					};
				} else if ( clip.method === 'pause' ) {
					videoAction = {
						onStart: () => {
							if ( clip.isJump ) {
								const currentTime = clip.timeFrom || 0;
								videoTarget.currentTime = currentTime;
							}
							videoTarget.pause();
						},
					};
				}
				timeline.to(
					videoTarget,
					{
						duration: clip.duration,
						ease: 'none',
						...videoAction,
					},
					tlPosition
				);
				break;
		}
	} );
	return [ timeline, textInstance ];
};

export const animationsKill = ( theElement, animations ) => {
	animations.forEach( ( animation ) => {
		const theClips = animation.clips;
		theClips.forEach( ( clip ) => {
			let theTargets = [];
			if ( clip.target ) {
				theTargets = [];
				clip.target.forEach( ( ele ) => {
					const getTargets = gsap.utils.toArray( ele );
					theTargets = theTargets.concat( getTargets );
				} );
			} else if ( theElement ) {
				theTargets = theElement;
			}
			const propsArray = [];
			if ( clip.from ) {
				for ( const key in clip.from ) {
					if ( clip.from.hasOwnProperty( key ) ) {
						if ( ! propsArray.includes( key ) ) {
							propsArray.push( key );
						}
					}
				}
			}
			if ( clip.to ) {
				for ( const key in clip.to ) {
					if ( clip.to.hasOwnProperty( key ) ) {
						if ( ! propsArray.includes( key ) ) {
							propsArray.push( key );
						}
					}
				}
			}
			const clearProps = propsArray.join( ',' );
			gsap.set( theTargets, { clearProps } );
		} );
	} );
};

const getTweenProgress = ( timeline, tween ) => {
	const totalDuration = timeline.duration();
	const tweenStart = tween.startTime();
	const tweenDuration = tween.duration();
	const timelineProgress = timeline.progress();
	const tweenProgress =
		( timelineProgress * totalDuration - tweenStart ) / tweenDuration;
	return tweenProgress;
};
